import { useState } from "react";
import moment from "moment-timezone";
import OwButton from "../OwButton/OwButton";
import FilterablePickerModal, { IPickerItem } from "../FilterablePickerModal/FilterablePickerModal";

/** IANA timezone names, computed once at module load. */
export const IANA_TIMEZONES: string[] = moment.tz.names();

/** The main US timezones, pinned to the top of the list — nearly all customers are US-based. */
const US_TIMEZONE_OPTIONS: IPickerItem[] = [
    { value: "America/New_York", label: "America/New_York", sublabel: "US Eastern" },
    { value: "America/Chicago", label: "America/Chicago", sublabel: "US Central" },
    { value: "America/Denver", label: "America/Denver", sublabel: "US Mountain" },
    { value: "America/Phoenix", label: "America/Phoenix", sublabel: "US Arizona (no DST)" },
    { value: "America/Los_Angeles", label: "America/Los_Angeles", sublabel: "US Pacific" },
    { value: "America/Anchorage", label: "America/Anchorage", sublabel: "US Alaska" },
    { value: "Pacific/Honolulu", label: "Pacific/Honolulu", sublabel: "US Hawaii" },
];

const US_TIMEZONE_VALUES = new Set(US_TIMEZONE_OPTIONS.map((tz) => tz.value));

/** Stable option list for the picker modal: US timezones first, then the rest alphabetically. */
export const IANA_TIMEZONE_OPTIONS: IPickerItem[] = [
    ...US_TIMEZONE_OPTIONS,
    ...IANA_TIMEZONES.filter((tz) => !US_TIMEZONE_VALUES.has(tz)).map((tz) => ({ value: tz, label: tz })),
];

type Props = {
    value: string | null | undefined;
    /** Called with the chosen IANA name, or "" when cleared. */
    onChange: (value: string) => void;
    /** When true (default), shows a clear button and a "None" entry in the modal. */
    allowClear?: boolean;
    ariaLabel?: string;
};

/**
 * Searchable timezone selector: a read-only input plus a "Select" button that opens a
 * FilterablePickerModal over the full IANA list. Native dropdowns are unusable at ~600 options.
 */
export const TimezonePicker = (props: Props) => {
    const [showPicker, setShowPicker] = useState(false);
    const allowClear = props.allowClear !== false;

    return (
        <>
            <div className={"d-flex align-items-center gap-2"}>
                <input
                    type={"text"}
                    readOnly={true}
                    aria-label={props.ariaLabel || "timezone"}
                    className={"form-control form-control-sm pointer"}
                    value={props.value || ""}
                    placeholder={"—"}
                    onClick={() => setShowPicker(true)}
                />
                <OwButton title={"select"} fn={() => setShowPicker(true)} className={"btn-outline-secondary btn-sm"} />
                {allowClear && props.value ? (
                    <OwButton
                        centerIconClass={"fas fa-times"}
                        ariaLabel={"clear timezone"}
                        fn={() => props.onChange("")}
                        className={"btn-outline-danger btn-sm"}
                    />
                ) : null}
            </div>
            <FilterablePickerModal
                isOpen={showPicker}
                title={"select_timezone"}
                searchPlaceholder={"search_timezones"}
                items={IANA_TIMEZONE_OPTIONS}
                selectedValue={props.value || undefined}
                allowClear={allowClear}
                onSelect={(value) => {
                    props.onChange(value);
                    setShowPicker(false);
                }}
                onClose={() => setShowPicker(false)}
            />
        </>
    );
};

export default TimezonePicker;
